import React, { useEffect, useState } from 'react';
import {
  AlertTriangle, CheckCircle2, Loader2, Network, Save, ServerCog, TestTube2,
} from 'lucide-react';
import client, { API } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { notifySettingsUpdated } from '../context/SettingsContext';

const SETTING_KEYS = [
  'game_gateway_enabled',
  'game_gateway_public_host',
  'game_gateway_dns_primary',
  'game_gateway_dns_secondary',
  'game_gateway_session_minutes',
  'game_gateway_bypass_vendors',
  'game_gateway_scorpio_enabled',
  'game_gateway_drakon_enabled',
  'game_gateway_goldpalace_enabled',
  'game_gateway_oroplay_enabled',
];

const GATEWAY_SOURCES = [
  ['game_gateway_scorpio_enabled', 'ScorpioPlay'],
  ['game_gateway_drakon_enabled', 'Drakon'],
  ['game_gateway_goldpalace_enabled', 'Gold Palace'],
  ['game_gateway_oroplay_enabled', 'OroPlay'],
];

// Gateway `/api` önekinin dışındadır; kökü API tabanından türetilir.
const gatewayRoot = () => API.replace(/\/api\/?$/, '');

/**
 * Ters vekilde `/gamelaunch` yolunun backend'e ulaşıp ulaşmadığını yalnızca
 * tarayıcı görebilir: yol tanımlı değilse istek SPA fallback'ine düşer ve
 * JSON yerine index.html gelir. Bu, gateway açıkken oyunların hiç açılmaması
 * demektir; sunucu tarafındaki hiçbir test bunu fark edemez.
 */
const probeGatewayPath = async (origin) => {
  const base = origin || gatewayRoot();
  try {
    const response = await fetch(`${base}/gamelaunch/health`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return { ok: false, reason: `HTTP ${response.status}` };
    const type = response.headers.get('content-type') || '';
    if (!type.includes('application/json')) {
      return { ok: false, reason: 'yol backend yerine siteye düşüyor' };
    }
    const data = await response.json();
    return data?.ok
      ? { ok: true }
      : { ok: false, reason: 'beklenmeyen yanıt' };
  } catch (error) {
    return { ok: false, reason: 'adrese ulaşılamadı' };
  }
};

const Field = ({ label, children, hint, full = false }) => (
  <label className={`gc-field ${full ? 'is-full' : ''}`}>
    <span className="gc-label">{label}</span>
    {children}
    {hint && <span className="gc-hint">{hint}</span>}
  </label>
);

// Test sonucunun her satırı ya tamam ya eksiktir; ara ton yoktur, çünkü
// eksik bir satır gateway açıkken oyunların hiç açılmaması demektir.
const Line = ({ ok, children }) => (
  <div className={`gc-line ${ok ? 'is-ok' : 'is-warn'}`}>
    {ok ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />}
    <span>{children}</span>
  </div>
);

const GameSystemSettingsPanel = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [gatewayStatus, setGatewayStatus] = useState(null);
  const [pathStatus, setPathStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    let active = true;
    client.get('/admin/settings')
      .then(({ data }) => { if (active) setSettings(data); })
      .catch((error) => {
        if (active) {
          toast({
            title: 'Oyun ayarları alınamadı',
            description: error.response?.data?.detail || 'İstek başarısız',
          });
        }
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [toast]);

  const update = (key, value) => {
    setSettings((current) => ({ ...current, [key]: value }));
  };

  const save = async () => {
    const payload = {};
    SETTING_KEYS.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(settings, key)) payload[key] = settings[key];
    });
    setSaving(true);
    try {
      const { data } = await client.patch('/admin/settings', payload);
      setSettings(data);
      notifySettingsUpdated();
      toast({ title: 'Oyun ve gateway ayarları kaydedildi' });
    } catch (error) {
      toast({
        title: 'Ayarlar kaydedilemedi',
        description: error.response?.data?.detail || 'İşlem başarısız',
      });
    } finally {
      setSaving(false);
    }
  };

  const testGateway = async () => {
    setTesting(true);
    setGatewayStatus(null);
    setPathStatus(null);
    try {
      // Kayıtlı alan adı denenir; henüz kaydedilmemiş bir değer için de
      // yöneticiye doğru sonucu göstermek adına kutudaki değer kullanılır.
      const host = (settings.game_gateway_public_host || '').trim();
      const [{ data }, path] = await Promise.all([
        client.get('/admin/games/gateway-status'),
        probeGatewayPath(host),
      ]);
      setGatewayStatus(data);
      setPathStatus(path);
      const healthy = data.connected && path.ok && !data.warning;
      toast({
        title: healthy ? 'Gateway hazır' : 'Gateway eksiksiz değil',
        description: data.error || (!path.ok ? `/gamelaunch yolu: ${path.reason}` : null)
          || data.warning
          || `${(data.resolved_addresses || []).length} DNS yanıtı alındı`,
      });
    } catch (error) {
      toast({
        title: 'Gateway testi başarısız',
        description: error.response?.data?.detail || 'Bağlantı kontrol edilemedi',
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="gc-state is-inline">
        <Loader2 size={20} className="gc-spin" />
        <p>Oyun sistemi ayarları yükleniyor…</p>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="gc-state">
        <AlertTriangle size={24} color="#fb7185" />
        <h2>Oyun sistemi ayarları yüklenemedi</h2>
        <p>İsteği yenileyin; sorun sürerse sunucu günlüğüne bakın.</p>
      </div>
    );
  }

  const gatewayOn = !!settings.game_gateway_enabled;
  const hasResult = gatewayStatus || pathStatus;

  return (
    <section className="gc-panel is-tone" style={{ '--gc-tone': '#38bdf8' }}>
      <div className="gc-panel-head">
        <span className="gc-card-icon"><Network size={19} /></span>
        <div className="gc-panel-title">
          <h2>Oyun Sistemi ve Gateway</h2>
          <p>
            Oyunların hangi adresten açılacağını, aynı-domain gateway ve DNS
            ayarlarını yönetin.
          </p>
        </div>
        <span className={`gc-badge ${gatewayOn ? 'is-on' : 'is-off'}`}>
          {gatewayOn ? 'Gateway açık' : 'Gateway kapalı'}
        </span>
      </div>

      <div className="gc-panel-body">
        <label className="gc-check">
          <input
            type="checkbox"
            checked={gatewayOn}
            onChange={(event) => update('game_gateway_enabled', event.target.checked)}
          />
          <span className="gc-check-text">
            <b>Seçili sağlayıcılarda gateway kullan</b>
            <small>
              Oyunlar <code>/gamelaunch/…</code> yolundan özel DNS ile açılır.
              Kapatılırsa sağlayıcının doğrudan launch adresi kullanılır. Açmadan
              önce ters vekilde <code>/gamelaunch/</code> yolunun tanımlı olduğunu
              aşağıdaki testle doğrulayın.
            </small>
          </span>
        </label>

        <div className="gc-fields is-3">
          <Field
            label="Gateway Alan Adı (isteğe bağlı)"
            full
            hint={(
              <>
                Boş bırakılırsa oyun ana site adresinden açılır ve sağlayıcının
                betiği siteyle <b>aynı origin’e</b> girer — oturum jetonunu
                okuyabilir. Ayrı bir alt alan adı yazılırsa bu risk kalkar, engel
                aşma faydası korunur. Alan adı aynı sunucuya çözmeli ve SSL
                sertifikası bulunmalıdır.
              </>
            )}
          >
            <input
              value={settings.game_gateway_public_host || ''}
              onChange={(event) => update('game_gateway_public_host', event.target.value)}
              className="gc-input"
              placeholder="https://oyun.siteniz.com"
            />
          </Field>

          <Field label="Birincil DNS">
            <input
              value={settings.game_gateway_dns_primary || '1.1.1.1'}
              onChange={(event) => update('game_gateway_dns_primary', event.target.value)}
              className="gc-input"
              placeholder="1.1.1.1"
            />
          </Field>

          <Field label="Yedek DNS">
            <input
              value={settings.game_gateway_dns_secondary || '1.0.0.1'}
              onChange={(event) => update('game_gateway_dns_secondary', event.target.value)}
              className="gc-input"
              placeholder="1.0.0.1"
            />
          </Field>

          <Field label="Oturum Süresi (dakika)">
            <input
              type="number"
              min="15"
              max="360"
              value={settings.game_gateway_session_minutes ?? 120}
              onChange={(event) => update('game_gateway_session_minutes', Number(event.target.value))}
              className="gc-input"
            />
          </Field>

          <Field
            label="Gateway Dışında Açılacak Üreticiler"
            full
            hint={(
              <>
                <b>sağlayıcı:üretici</b> biçiminde, virgülle ayırın. Bu oyunlar
                sağlayıcının kendi adresinden açılır; varlık adreslerini kendi
                sunucu adlarından türettikleri için gateway altında yükleme
                ekranında kalıyorlar. Sağlayıcı öneki yazılmazsa aynı adlı üretici
                tüm sağlayıcılarda devre dışı kalır — <b>amusnet</b> hem Drakon’da
                hem Gold Palace’ta bulunduğu için öneki yazın. Yalnızca doğrudan
                erişilebilen üreticileri ekleyin.
              </>
            )}
          >
            <input
              value={settings.game_gateway_bypass_vendors || ''}
              onChange={(event) => update('game_gateway_bypass_vendors', event.target.value)}
              className="gc-input"
              placeholder="goldpalace:Amusnet"
            />
          </Field>
        </div>

        <div className="gc-box">
          <div className="gc-box-head">
            <div>
              <h3>Gateway’den geçecek sağlayıcılar</h3>
              <p>İşareti kaldırılan sağlayıcı kendi launch adresinden açılır.</p>
            </div>
          </div>
          <div className="gc-checks">
            {GATEWAY_SOURCES.map(([key, label]) => (
              <label key={key} className="gc-check is-plain">
                <input
                  type="checkbox"
                  checked={settings[key] !== false}
                  onChange={(event) => update(key, event.target.checked)}
                />
                <span className="gc-check-text">{label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="gc-box">
          <div className="gc-box-head">
            <div>
              <h3>DNS ve yol testi</h3>
              <p>
                Ters vekildeki <code>/gamelaunch/</code> yolu ile sağlayıcı
                adreslerinin çözümlenmesini birlikte sınar.
              </p>
            </div>
            <button
              type="button"
              onClick={testGateway}
              disabled={testing}
              className="gc-btn is-tone gc-spacer"
            >
              {testing ? <Loader2 size={14} className="gc-spin" /> : <TestTube2 size={14} />}
              {testing ? 'Sınanıyor…' : 'DNS ve Gateway Testi'}
            </button>
          </div>

          {hasResult ? (
            <div className="gc-lines">
              {pathStatus && (
                <Line ok={pathStatus.ok}>
                  {pathStatus.ok
                    ? '/gamelaunch yolu backend’e ulaşıyor'
                    : `/gamelaunch yolu backend’e ulaşmıyor (${pathStatus.reason}) — ters vekile proxy bloğu eklenmeli, gateway açıksa oyunlar açılmaz`}
                </Line>
              )}
              {gatewayStatus && (
                <>
                  <Line ok={!!gatewayStatus.secret_ok}>
                    {gatewayStatus.secret_ok
                      ? `İmza anahtarı tamam${gatewayStatus.secret_dedicated ? '' : ' (JWT_SECRET kullanılıyor)'}`
                      : 'İmza anahtarı eksik veya kısa — oyunlar 503 alır'}
                  </Line>
                  <Line ok={!!gatewayStatus.connected}>
                    {gatewayStatus.connected
                      ? `DNS çözümlemesi çalışıyor · ${gatewayStatus.active_sessions || 0} aktif oturum`
                      : (gatewayStatus.error || 'DNS resolver bağlantısı kurulamadı')}
                  </Line>
                  {(gatewayStatus.upstreams || []).map((row) => (
                    <Line key={row.provider} ok={row.ok}>
                      {row.ok
                        ? `${row.provider}: ${row.host} erişilebilir (HTTP ${row.status})`
                        : `${row.provider}: ${row.host} erişilemiyor — ${row.error || 'bağlantı kurulamadı'}`}
                    </Line>
                  ))}
                  {gatewayStatus.warning && <Line ok={false}>{gatewayStatus.warning}</Line>}
                </>
              )}
            </div>
          ) : (
            <p className="gc-note">
              <ServerCog size={14} />
              Test yalnızca tarayıcıdan görülebilen bir kontroldür: sunucu
              tarafındaki hiçbir denetim <code>/gamelaunch/</code> yolunun ters
              vekilde tanımlı olup olmadığını göremez.
            </p>
          )}
        </div>
      </div>

      <div className="gc-panel-foot">
        <span className="gc-foot-note">
          Ayarlar kaydedildiği anda geçerlidir; açık oyun oturumları etkilenmez.
        </span>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="gc-btn is-primary"
        >
          {saving ? <Loader2 size={14} className="gc-spin" /> : <Save size={14} />}
          Oyun Sistemi Ayarlarını Kaydet
        </button>
      </div>
    </section>
  );
};

export default GameSystemSettingsPanel;
